import { parseInt } from 'lodash';

import { DEFAULT_CORNER_RADIUS } from '@common/constants';
import { clamp } from '@common/helpers';
import { CommonProductModel } from '@common/models';
import { Product } from '@common/utils';

export enum RadiusRangeKeys {
    R0 = 'R0',
    R1 = 'R1',
    R2 = 'R2',
    R3 = 'R3',
    R4 = 'R4',
    R5 = 'R5',
}

export const RADIUS_MAX_DEFAULT_VALUE = 43;

export type RadiusRangeValues = [number, number];
export type RadiusRangeMap = {
    [key in RadiusRangeKeys]: RadiusRangeValues;
};
export const RADIUS_FAKE_INFINITY = 5000;

/**
 * These are the valid ranges for radius based on the R[0-5] qteCode
 * The test is min > value <= max
 * @see getCornerRadiusFromSize
 */
export const RADIUS_RANGES: RadiusRangeMap = {
    R0: [0, 0.75],
    R1: [0.75, 6],
    R2: [6, 18],
    R3: [18, 30],
    R4: [30, 42],
    R5: [42, RADIUS_FAKE_INFINITY],
};

const EMPTY_RANGE = [0, 0] satisfies RadiusRangeValues;

export interface CornerRadiusModel extends CommonProductModel {
    imageUrl: string;
    priceWithoutDiscount: number;
    range?: string;
}

export function isCornerRadiusModel(obj: Product): boolean {
    return 'range' in obj || !!(obj.qteCode && obj.qteCode in RadiusRangeKeys);
}

export const parseRangeString = (range: string | undefined): RadiusRangeValues => {
    if (!range) {
        return EMPTY_RANGE;
    }
    // [a,b] or [a,] where a and b are numbers
    const matches = range.matchAll(/\[([\d.]+),([\d.]*)]/g).next()?.value;
    if (!matches) {
        return EMPTY_RANGE;
    }
    return [parseInt(matches[1]), parseInt(matches[2] ?? RADIUS_FAKE_INFINITY)];
};

export const getRangeFromQteCode = (qteCode: string | null | undefined): RadiusRangeValues | undefined => {
    return qteCode && qteCode in RadiusRangeKeys ? RADIUS_RANGES[qteCode as RadiusRangeKeys] : undefined;
};

/** ensure the min/max values are _at least_ the DEFAULT_CORNER_RADIUS
 * @param range the range to be constrained
 */
export const constrainMinMaxToDefaultMinimum = (range: RadiusRangeValues): RadiusRangeValues =>
    range.map((val) => Math.max(val, DEFAULT_CORNER_RADIUS)) as RadiusRangeValues;

/**
 * get the min/max range for a given cornerRadiusModel product based on qteCode (preferred) or range (old style) or [0,0]
 * @param radiusProduct
 */
export const getMinMaxFromCorner = (radiusProduct: CornerRadiusModel): RadiusRangeValues => {
    if (!radiusProduct.range && !radiusProduct.qteCode) {
        return EMPTY_RANGE;
    }
    return getRangeFromQteCode(radiusProduct.qteCode) ?? parseRangeString(radiusProduct.range);
};
export const getMinFromRange = (radiusProduct: CornerRadiusModel): number => getMinMaxFromCorner(radiusProduct)[0];
export const getMaxFromRange = (radiusProduct: CornerRadiusModel): number => getMinMaxFromCorner(radiusProduct)[1];

/**
 * return the default radius that should be set based upon the range details of a RadiusCorner product
 * range will be based on qteCode (preferred) OR range (old style)
 * radius will be the max value of the range
 * the radius will not be < DEFAULT_CORNER_RADIUS
 * the radius will not exceed RADIUS_MAX_DEFAULT_VALUE
 * @param radiusProduct
 */
export const getDefaultRadiusFromRadii = (radiusProduct: CornerRadiusModel): number => {
    const rangeMax = getMaxFromRange(radiusProduct);
    return clamp(rangeMax, DEFAULT_CORNER_RADIUS, RADIUS_MAX_DEFAULT_VALUE);
};
